/**
 * TanStack Query key factories for Otto. All keys include `tenantId` for
 * cache isolation per the memorybank convention.
 */

import type {OttoSessionId, PageType} from './types/otto-models';

export const ottoSessionKeys = {
  all: ['otto-sessions'] as const,
  /**
   * Active session for a (tenant, pageType) pair. Sessions are sticky
   * across list and detail surfaces of the same page type.
   */
  current: (tenantId: string, pageType: PageType) =>
    [...ottoSessionKeys.all, 'current', tenantId, pageType] as const,
  /**
   * Partial-prefix key alias kept for callers that previously needed to
   * invalidate every `current` variant for a (tenant, pageType).
   */
  currentScope: (tenantId: string, pageType: PageType) =>
    [...ottoSessionKeys.all, 'current', tenantId, pageType] as const,
  detail: (tenantId: string, sessionId: OttoSessionId) =>
    [...ottoSessionKeys.all, 'detail', tenantId, sessionId] as const,
  /** All non-deleted sessions for a (tenant, pageType) — both ACTIVE and
   *  ARCHIVED rows. The conversations list reads from this. */
  list: (tenantId: string, pageType: PageType) =>
    [...ottoSessionKeys.all, 'list', tenantId, pageType] as const,
};

export const ottoMessageKeys = {
  all: ['otto-messages'] as const,
  /** Persisted history for a session — read on mount by useOttoSession. */
  history: (tenantId: string, sessionId: OttoSessionId) =>
    [...ottoMessageKeys.all, 'history', tenantId, sessionId] as const,
};

export const ottoSharedChatKeys = {
  all: ['otto-shared-chats'] as const,
  /** A frozen shared snapshot, keyed by its public share id. */
  detail: (shareId: string) =>
    [...ottoSharedChatKeys.all, 'detail', shareId] as const,
};

export const ottoRunKeys = {
  all: ['otto-runs'] as const,
  /** Liveness of a session's background run — polled during reattach. */
  status: (tenantId: string, sessionId: OttoSessionId) =>
    [...ottoRunKeys.all, 'status', tenantId, sessionId] as const,
  /** Set of the user's sessions with an in-flight run for a page type —
   *  polled by the conversations list for the per-row activity spinner. */
  activeIds: (tenantId: string, pageType: PageType) =>
    [...ottoRunKeys.all, 'active-ids', tenantId, pageType] as const,
};
